/**
 * Authoritative water-quality domain model (client side).
 *
 * Single source of truth for the slot × param matrix, client-safe validation
 * tiers (bands come from the farm's threshold table), the pH dial, and DOC-aware
 * completion — mirrors the backend validation service (app/services/validation.py).
 *
 * Invariants:
 * - NULL ≠ 0: a blank is `null` and is never coerced to 0.
 * - Hard plausibility limits are SERVER-SIDE ONLY. The client computes
 *   ok/warning/critical from the farm's recommended + critical bands (both client-safe);
 *   the `reject` tier is known only from a 400 on save — never modelled here.
 * - pH is a single band. The swing is the pH dial: 3pm − 7am.
 */
import type { ReadingSlot } from '@/types/entry';
import { WATER_COLOR_OPTIONS, WEATHER_OPTIONS } from '@/lib/constants';
import {
  evaluateTier,
  recommendedBand,
  recommendedLabel,
  usualRangeSentence,
  type Context,
  type ThresholdTable,
  type Tier,
} from '@/lib/thresholds';

export const ALL_SLOTS: ReadingSlot[] = ['5am', '7am', '3pm', '7pm', '10pm'];

export type SlotType = 'DO' | 'FULL';
export const SLOT_META: Record<ReadingSlot, { label: string; type: SlotType; purpose: string }> = {
  '5am': { label: '5am', type: 'DO', purpose: 'Dawn DO check' },
  '7am': { label: '7am', type: 'FULL', purpose: 'Morning baseline' },
  '3pm': { label: '3pm', type: 'FULL', purpose: 'Afternoon peak + chemicals' },
  '7pm': { label: '7pm', type: 'DO', purpose: 'Evening DO check' },
  '10pm': { label: '10pm', type: 'DO', purpose: 'Night DO check' },
};

const SLOT_START_MINUTES: Record<ReadingSlot, number> = {
  '5am': 5 * 60,
  '7am': 7 * 60,
  '3pm': 15 * 60,
  '7pm': 19 * 60,
  '10pm': 22 * 60,
};
// A round stays "current" this long after its clock time, then the next one is up.
const ROUND_GRACE_MINUTES = 120;

/** The water round in progress or next up at a PH-local time of day (minutes). */
export function roundSlotAt(minutesOfDay: number): ReadingSlot {
  return ALL_SLOTS.find((s) => minutesOfDay < SLOT_START_MINUTES[s] + ROUND_GRACE_MINUTES) ?? '10pm';
}

export type ParamType = 'number' | 'select';
export interface ParamDef {
  key: string; // UI key (camelCase)
  label: string;
  short: string; // compact label for the grid row
  unit?: string;
  dbColumn: string; // snake_case API/DB column
  type: ParamType;
  options?: { value: string; label: string }[];
  step?: number;
  /** Digits after the point the water_readings column keeps (its numeric scale); more are rounded away. */
  decimals?: number;
  /** The key of the param that completes this one's pair ("100/100" typed in the first fills both). */
  pairSecondKey?: string;
}

/** Ordered measurement rows for the single-pond grid (matrix order). */
export const GRID_PARAMS: ParamDef[] = [
  { key: 'dissolvedOxygen', label: 'Dissolved Oxygen', short: 'DO', unit: 'mg/L', dbColumn: 'dissolved_oxygen', type: 'number', step: 0.1, decimals: 2 },
  { key: 'ph', label: 'pH', short: 'pH', dbColumn: 'ph', type: 'number', step: 0.1, decimals: 2 },
  { key: 'temperature', label: 'Temperature', short: 'Temp', unit: '°C', dbColumn: 'temperature_c', type: 'number', step: 0.1, decimals: 2 },
  { key: 'salinity', label: 'Salinity', short: 'Sal', unit: 'ppt', dbColumn: 'salinity_ppt', type: 'number', step: 1, decimals: 1 },
  { key: 'depth', label: 'Water Depth', short: 'Depth', unit: 'm', dbColumn: 'depth_m', type: 'number', step: 0.1, decimals: 2 },
  { key: 'transparency', label: 'Transparency', short: 'Transp', unit: 'cm', dbColumn: 'transparency_cm', type: 'number', step: 1, decimals: 0 },
  { key: 'alkalinity', label: 'Alkalinity', short: 'Alk', unit: 'ppm', dbColumn: 'alkalinity_ppm', type: 'number', step: 1, decimals: 0, pairSecondKey: 'alkalinity2' },
  { key: 'alkalinity2', label: 'Alkalinity 2', short: 'Alk 2', unit: 'ppm', dbColumn: 'alkalinity_2', type: 'number', step: 1, decimals: 0 },
  { key: 'calcium', label: 'Cal/Mag', short: 'Cal/Mag', unit: 'ppm', dbColumn: 'calcium_ppm', type: 'number', step: 1, decimals: 0, pairSecondKey: 'magnesium' },
  { key: 'magnesium', label: 'Magnesium', short: 'Magnesium', unit: 'ppm', dbColumn: 'magnesium_ppm', type: 'number', step: 1, decimals: 0 },
  { key: 'ammonia', label: 'Ammonia (NH3)', short: 'NH3', unit: 'ppm', dbColumn: 'ammonia_ppm', type: 'number', step: 0.01, decimals: 3 },
  { key: 'nitrite', label: 'Nitrite (NO2)', short: 'NO2', unit: 'ppm', dbColumn: 'nitrite_ppm', type: 'number', step: 0.01, decimals: 3 },
];

/** Observation fields (7am & 3pm only) — never gate completion. */
export const OBSERVATION_PARAMS: ParamDef[] = [
  { key: 'waterColor', label: 'Water Color', short: 'Color', dbColumn: 'water_color', type: 'select', options: WATER_COLOR_OPTIONS },
  { key: 'weather', label: 'Weather', short: 'Weather', dbColumn: 'weather', type: 'select', options: WEATHER_OPTIONS },
];

const PARAM_BY_KEY: Record<string, ParamDef> = Object.fromEntries(
  [...GRID_PARAMS, ...OBSERVATION_PARAMS].map((p) => [p.key, p])
);
export function paramByKey(key: string): ParamDef | undefined {
  return PARAM_BY_KEY[key];
}
/**
 * "Clear to bottom" (#374) rides in the transparency cell as this value instead of a number, so
 * every screen that treats a filled cell as done keeps working. It is never a measurement: tiers,
 * advice and charts skip it. On the wire it is `transparency_clear_to_bottom: true` with
 * `transparency_cm: null`.
 */
export const CLEAR_TO_BOTTOM = 'clear';

export function isClearToBottom(value: unknown): boolean {
  return value === CLEAR_TO_BOTTOM;
}

/** Translate a batch of cell edits into the API body: the transparency cell is one value here, two columns there. */
export function toWirePayload<T extends Record<string, unknown>>(payload: T): Record<string, unknown> {
  if (!('transparency_cm' in payload)) return payload;
  return isClearToBottom(payload.transparency_cm)
    ? { ...payload, transparency_cm: null, transparency_clear_to_bottom: true }
    : { ...payload, transparency_clear_to_bottom: null };
}

export function dbColumnFor(key: string): string {
  return PARAM_BY_KEY[key]?.dbColumn ?? key;
}

/**
 * Round a typed number to the digits its column keeps (numeric(4,1) holds 7.5, not 7.55),
 * so what the screen shows after saving is what the database holds. Rounds half up on the
 * decimal value, as Postgres does, not on its binary float.
 */
export function roundToColumnScale(paramKey: string, value: number): number {
  const decimals = PARAM_BY_KEY[paramKey]?.decimals;
  if (decimals === undefined || !Number.isFinite(value)) return value;
  const text = String(value);
  if (text.includes('e')) return Number(value.toFixed(decimals));
  const sign = value < 0 ? -1 : 1;
  return sign * Number(`${Math.round(Number(`${Math.abs(value)}e${decimals}`))}e-${decimals}`);
}

/**
 * Applicability matrix — which param keys are EDITABLE at each slot. Anything not
 * listed is N/A at that slot (greyed, non-focusable em-dash). This is the BUG-13
 * affordance fix: editable vs N/A is structural, never ambiguous.
 *
 *   DO                                      → 5am, 3pm, 7pm, 10pm  (NOT 7am)
 *   pH, Temp, Salinity, Depth, Transparency → 7am, 3pm
 *   Alkalinity 1/2, Calcium, Magnesium, Ammonia, Nitrite → 3pm only
 *   Color, Weather (observations)           → 7am, 3pm
 */
export const SLOT_PARAMS: Record<ReadingSlot, string[]> = {
  '5am': ['dissolvedOxygen'],
  '7am': ['ph', 'temperature', 'salinity', 'depth', 'transparency', 'waterColor', 'weather'],
  '3pm': ['dissolvedOxygen', 'ph', 'temperature', 'salinity', 'depth', 'transparency', 'alkalinity', 'alkalinity2', 'calcium', 'magnesium', 'ammonia', 'nitrite', 'waterColor', 'weather'],
  '7pm': ['dissolvedOxygen'],
  '10pm': ['dissolvedOxygen'],
};

/** A farm's field tests not taken at a slot (settings.water_fields_not_measured, #371): slot -> reading columns. */
export type SlotFieldsOff = Partial<Record<string, string[]>>;

/** A pair (alkalinity 1/2, calcium/magnesium) is ONE cell/field showing and taking "x/y"; stored as two columns. */
const PAIR_SECOND_KEYS = new Set(['alkalinity2', 'magnesium']);
/** The other column of a pair (alkalinity_ppm <-> alkalinity_2, calcium_ppm <-> magnesium_ppm), or undefined. */
export const pairPartnerColumn = (column: string): string | undefined => {
  for (const p of GRID_PARAMS) {
    const second = p.pairSecondKey ? GRID_PARAMS.find((x) => x.key === p.pairSecondKey) : undefined;
    if (!second) continue;
    if (p.dbColumn === column) return second.dbColumn;
    if (second.dbColumn === column) return p.dbColumn;
  }
  return undefined;
};
export const isPairSecond = (paramKey: string): boolean => PAIR_SECOND_KEYS.has(paramKey);
/** The grid's rows: every param except a pair's second half (it lives in its first's cell). */
export const GRID_ROWS: ParamDef[] = GRID_PARAMS.filter((p) => !isPairSecond(p.key));
/** The first param of the pair a column belongs to (itself when it is not a second half). */
export function ownerParam(dbColumn: string): ParamDef | undefined {
  const p = [...GRID_PARAMS, ...OBSERVATION_PARAMS].find((x) => x.dbColumn === dbColumn);
  if (!p) return undefined;
  return GRID_PARAMS.find((x) => x.pairSecondKey === p.key) ?? p;
}

export function isApplicable(paramKey: string, slot: ReadingSlot, off?: SlotFieldsOff): boolean {
  if (!SLOT_PARAMS[slot]?.includes(paramKey)) return false;
  return !off?.[slot]?.includes(dbColumnFor(paramKey));
}

/** A field the slot takes in general but this farm turned off there (#371), as opposed to one the slot never takes. */
export function isFarmOff(paramKey: string, slot: ReadingSlot, off?: SlotFieldsOff): boolean {
  return !!SLOT_PARAMS[slot]?.includes(paramKey) && !isApplicable(paramKey, slot, off);
}

/** "Salinity is not measured at 3pm on this farm." */
export function notMeasuredSentence(p: ParamDef, slot: ReadingSlot): string {
  return `${p.label} is not measured at ${SLOT_META[slot].label} on this farm.`;
}

/**
 * What to type in an empty entry box, from the field's step: 0.1 -> "0.0", 1 -> "00"; a pair
 * shows both halves ("000/000"). A hint only, never a value.
 */
export function entryHint(p: ParamDef): string {
  const fraction = String(p.step ?? 1).split('.')[1]?.length ?? 0;
  const one = fraction > 0 ? `0.${'0'.repeat(fraction)}` : '00';
  return p.pairSecondKey ? `0${one}/0${one}` : one;
}

/**
 * Lab tests (Dan, #194): optional extras run in the lab. They never make a slot
 * started or done; only the field tests the slot requires do.
 */
export const LAB_PARAM_KEYS = new Set(['alkalinity', 'alkalinity2', 'calcium', 'magnesium', 'ammonia', 'nitrite']);

export function fieldParamsFor(slot: ReadingSlot, off?: SlotFieldsOff): ParamDef[] {
  return GRID_ROWS.filter((p) => isApplicable(p.key, slot, off) && !LAB_PARAM_KEYS.has(p.key));
}

/** Every param editable at a slot (field tests, lab tests, observations), in screen order. */
export function fieldsFor(slot: ReadingSlot, off?: SlotFieldsOff): ParamDef[] {
  return [...GRID_ROWS, ...OBSERVATION_PARAMS].filter((p) => isApplicable(p.key, slot, off));
}

// --------------------------------------------------------------------------- //
// Validation tiers. The bands are the farm's own table (GET /farms/{id}/thresholds,
// lib/thresholds.ts); this file only maps a grid param key to its reading column.
// No hard limits anywhere client-side — those stay server-side.
// --------------------------------------------------------------------------- //

export type { Tier } from '@/lib/thresholds';

/** Tier for a grid param (`ph`, `dissolvedOxygen`, ...). No table yet means no colour. */
export function tierFor(
  table: ThresholdTable | undefined,
  paramKey: string,
  value: number | null | undefined,
  ctx?: Context
): Tier {
  return evaluateTier(table, dbColumnFor(paramKey), value, ctx);
}

/** Human inline-helper string for a param's recommended range, e.g. "rec 7.8–8.2". */
export function recommendedLabelFor(table: ThresholdTable | undefined, paramKey: string, ctx?: Context): string | null {
  return recommendedLabel(table, dbColumnFor(paramKey), ctx);
}

/**
 * Plain-words usual range for a param, from the recommended band only (never a
 * hard limit): "Usual range: 7.8 to 8.2.", "Usual: at least 5.", "Usual: up to 1.0."
 * Null when the param has no range.
 */
export function usualRange(table: ThresholdTable | undefined, paramKey: string, ctx?: Context): string | null {
  return usualRangeSentence(table, dbColumnFor(paramKey), (PARAM_BY_KEY[paramKey]?.step ?? 1) < 1, ctx);
}

/** What a field's column keeps after the point, for the keypad's refusal hint: "Salinity takes 1 decimal". */
export function decimalLimitHint(paramKey: string): string | null {
  const p = PARAM_BY_KEY[paramKey];
  if (!p || p.decimals === undefined) return null;
  if (p.decimals === 0) return `${p.label} takes whole numbers`;
  return `${p.label} takes ${p.decimals} decimal${p.decimals === 1 ? '' : 's'}`;
}

/** Exactly 0 is how a missed measurement gets typed; the server refuses it for these two. */
const ZERO_MEANS_NOT_MEASURED = new Set(['depth', 'transparency']);

/**
 * The likely mistake behind a refused value, from the shown value and the farm's usual (yellow)
 * range only, so it never hints at the server's hidden limit. A value three times past the usual
 * top (or, where there is no top, the usual floor) is a missing decimal point, an extra digit in a whole-number
 * column, or the wrong unit.
 */
function likelyMistake(table: ThresholdTable | undefined, paramKey: string, value: number): string | null {
  const band = recommendedBand(table, dbColumnFor(paramKey));
  const scale = band ? (band.max ?? band.min) : null;
  if (scale === null || scale <= 0 || !(value >= scale * 3)) return null;
  switch (paramKey) {
    case 'depth':
      return `Depth is in metres; ${value} looks like centimetres.`;
    case 'transparency':
      return 'Transparency is in centimetres; check it is not millimetres.';
    case 'salinity':
      return 'Salinity is in ppt; check it is not ppm or a conductivity reading.';
    case 'alkalinity':
      return 'Alkalinity is in ppm; check it is not a different unit or two readings added together.';
    default:
      // A whole-number column (alkalinity, calcium, magnesium) has no decimal point to miss.
      return PARAM_BY_KEY[paramKey]?.decimals === 0
        ? `${value} may have an extra digit.`
        : `${value} may be missing its decimal point.`;
  }
}

/**
 * The message for a value the server refused, shared by phone, desktop and the pre-stocking
 * check form: which value, its unit, the likely mistake, the usual range, and what to do:
 * "Water Depth 120 m isn't accepted. Depth is in metres; 120 looks like centimetres. Usual range:
 * 1.2 to 1.5. Re-enter it or leave it blank." The range is the farm's recommended band only; the
 * server's hard limit is never named or hinted at.
 */
export function refusedValueMessage(table: ThresholdTable | undefined, paramKey: string, shownValue: string): string {
  const def = PARAM_BY_KEY[paramKey];
  const label = def?.label ?? paramKey;
  const unit = def?.unit ? ` ${def.unit}` : '';
  const n = Number(shownValue);
  if (ZERO_MEANS_NOT_MEASURED.has(paramKey) && n === 0) {
    return `${label} 0${unit} isn't accepted. Leave it blank if you didn't measure it.`;
  }
  const mistake = Number.isFinite(n) ? likelyMistake(table, paramKey, n) : null;
  const usual = usualRange(table, paramKey);
  return [`${label} ${shownValue}${unit} isn't accepted.`, mistake, usual, 'Re-enter it or leave it blank.'].filter(Boolean).join(' ');
}

// --------------------------------------------------------------------------- //
// pH dial: Δ = pH(3pm) − pH(7am), warn |Δ| > 0.3
// --------------------------------------------------------------------------- //

export const PH_DIAL_THRESHOLD = 0.3;

export function computePhDial(
  ph7am: number | null | undefined,
  ph3pm: number | null | undefined
): { delta: number | null; warning: boolean } {
  if (ph7am === null || ph7am === undefined || ph3pm === null || ph3pm === undefined) {
    return { delta: null, warning: false };
  }
  const delta = Math.round((ph3pm - ph7am) * 100) / 100;
  return { delta, warning: Math.abs(delta) > PH_DIAL_THRESHOLD };
}

// --------------------------------------------------------------------------- //
// Completion — informational + DOC-aware. Mirrors Stage-1 daily_core_completion.
// Never blocks submit; never counts params not yet started.
// --------------------------------------------------------------------------- //

export const DO_SLOTS: ReadingSlot[] = ['5am', '3pm', '7pm', '10pm'];
export const CORE_PH_TEMP_SLOTS: ReadingSlot[] = ['7am', '3pm'];
export const DEFAULT_DO_START_DOC = 10;

/** A day's readings keyed by slot; each value is a record of dbColumn → value. */
export type ReadingsBySlot = Partial<Record<ReadingSlot, Record<string, number | string | null>>>;

export interface Completion {
  expected: number;
  present: number;
  complete: boolean;
  missing: string[];
}

export function dailyCoreCompletion(
  readingsBySlot: ReadingsBySlot,
  doc: number | null,
  doStartDoc: number = DEFAULT_DO_START_DOC
): Completion {
  const expected: Array<[ReadingSlot, string]> = [];
  if (doc === null || doc >= doStartDoc) {
    for (const s of DO_SLOTS) expected.push([s, 'dissolved_oxygen']);
  }
  for (const s of CORE_PH_TEMP_SLOTS) {
    expected.push([s, 'ph'], [s, 'temperature_c']);
  }
  const missing = expected
    .filter(([slot, col]) => (readingsBySlot[slot] ?? {})[col] === null || (readingsBySlot[slot] ?? {})[col] === undefined)
    .map(([slot, col]) => `${slot}:${col}`);
  return {
    expected: expected.length,
    present: expected.length - missing.length,
    complete: missing.length === 0,
    missing,
  };
}

export type DayStatus = 'complete' | 'partial' | 'critical' | 'empty';

/** Does any applicable numeric param in the day's readings sit in the critical tier? */
export function hasCritical(table: ThresholdTable | undefined, readingsBySlot: ReadingsBySlot, off?: SlotFieldsOff): boolean {
  for (const slot of ALL_SLOTS) {
    const row = readingsBySlot[slot];
    if (!row) continue;
    for (const p of GRID_PARAMS) {
      if (p.type !== 'number' || !isApplicable(p.key, slot, off)) continue;
      const raw = row[p.dbColumn];
      const v = raw === null || raw === undefined || isClearToBottom(raw) ? null : Number(raw);
      if (v !== null && tierFor(table, p.key, v, row) === 'critical') return true;
    }
  }
  return false;
}

/** Classify a whole day for the fill-map / all-ponds chips. */
export function dayStatus(
  table: ThresholdTable | undefined,
  readingsBySlot: ReadingsBySlot,
  doc: number | null,
  doStartDoc: number = DEFAULT_DO_START_DOC,
  off?: SlotFieldsOff
): DayStatus {
  const anyValue = ALL_SLOTS.some((slot) => {
    const row = readingsBySlot[slot];
    return row && Object.values(row).some((v) => v !== null && v !== undefined && v !== '');
  });
  if (!anyValue) return 'empty';
  if (hasCritical(table, readingsBySlot, off)) return 'critical';
  return dailyCoreCompletion(readingsBySlot, doc, doStartDoc).complete ? 'complete' : 'partial';
}
